/*
  Revision 21: card photos, mixed rule (owner decision 23 Oct).
  - Product-only photo on a plain white or near-white ground (packshot): shown whole
    (object-fit: contain, centred). The whole box is painted with the photo's own white,
    read from its edge pixels, so every card box looks equally full.
  - Every other photo (model, grey / beige / gradient studio ground): the 22 Oct rule stays,
    the photo fills the box (cover, centre top; feet and floor may be cut at the bottom).
  - If a photo cannot be read it stays on the fill rule.
  Each photo is judged on its own, the hover photo too. No store data is read or written;
  new products work on their own. The verdict is kept in localStorage, so the next page view
  shows the right fit before the photo arrives. CSS: base.css "Revision 21".
*/
(() => {
  const root = document.documentElement;
  if (!root.classList.contains('menam-ground')) return;
  window.menamGround = true;

  const SELECTOR = [
    '.card-gallery img.product-media__image',
    '.menam-subcollections__media img',
    '.predictive-search-results .resource-card[data-resource-type="product"] img.resource-card__image',
  ].join(',');
  // Boxes that hold exactly one photo; a packshot paints its white on the box as well
  const BOX = '.card-gallery .product-media-container, .menam-subcollections__media';

  const SAMPLES = 24; // points read along each edge
  const STORE_KEY = 'menam-fit-v2';
  const done = new WeakSet();

  let store = {};
  try {
    store = JSON.parse(localStorage.getItem(STORE_KEY) || '{}') || {};
  } catch (e) {
    store = {};
  }

  let saveTimer = 0;
  const save = () => {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      try {
        const keys = Object.keys(store);
        if (keys.length > 800) keys.slice(0, keys.length - 800).forEach((k) => delete store[k]);
        localStorage.setItem(STORE_KEY, JSON.stringify(store));
      } catch (e) {
        /* storage full or blocked: the photo is simply read again next time */
      }
    }, 400);
  };

  /** Same photo at any width shares one key (file path + version). */
  const keyOf = (img) => {
    const src = img.currentSrc || img.getAttribute('src') || '';
    if (!src) return '';
    try {
      const url = new URL(src, location.href);
      return url.pathname + '?v=' + (url.searchParams.get('v') || '');
    } catch (e) {
      return '';
    }
  };

  let canvas = null;
  let ctx = null;

  /**
   * Average colours along one edge strip, read at the photo's own resolution.
   * `side`: 0 left, 1 right, 2 top, 3 bottom. Transparent pixels are skipped; a mostly
   * transparent edge (cut-out PNG) returns null.
   */
  const readEdge = (source, w, h, side) => {
    const vertical = side < 2;
    const t = Math.max(2, Math.round((vertical ? w : h) * 0.01)); // outer 1% of the photo
    const sx = side === 1 ? w - t : 0;
    const sy = side === 3 ? h - t : 0;
    const sw = vertical ? t : w;
    const sh = vertical ? h : t;
    canvas.width = sw;
    canvas.height = sh;
    ctx.clearRect(0, 0, sw, sh);
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh);
    const d = ctx.getImageData(0, 0, sw, sh).data;
    const length = vertical ? sh : sw;
    const bins = [];
    let opaque = 0;
    for (let s = 0; s < SAMPLES; s++) {
      const from = Math.floor((s * length) / SAMPLES);
      const to = Math.max(from + 1, Math.floor(((s + 1) * length) / SAMPLES));
      let r = 0;
      let g = 0;
      let b = 0;
      let n = 0;
      for (let a = from; a < to; a++) {
        for (let k = 0; k < t; k++) {
          const i = (vertical ? a * sw + k : k * sw + a) * 4;
          if (d[i + 3] < 250) continue;
          r += d[i];
          g += d[i + 1];
          b += d[i + 2];
          n++;
        }
      }
      opaque += n;
      if (n) bins.push([r / n, g / n, b / n]);
    }
    if (opaque < sw * sh * 0.3) return null;
    return bins;
  };

  const isWhite = (c) => Math.min(...c) >= 236 && Math.max(...c) - Math.min(...c) <= 10;

  /**
   * Verdict for one decoded photo: { fit: 'packshot', c: [r, g, b] } when all four edges are a
   * plain white ground, otherwise { fit: 'cover' }. null when the photo cannot be read.
   */
  const judge = (source, w, h) => {
    if (!canvas) {
      canvas = document.createElement('canvas');
      ctx = canvas.getContext('2d', { willReadFrequently: true });
    }
    const edges = [0, 1, 2, 3].map((side) => readEdge(source, w, h, side));
    if (edges.some((e) => !e || !e.length)) return null;
    // The ground is white all round: at least 90% of the edge points, and every edge at least
    // 60% (a sleeve, hem or leg may touch one edge)
    const share = edges.map((e) => e.filter(isWhite).length / e.length);
    const all = edges.flat().filter(isWhite);
    if (Math.min(...share) < 0.6 || all.length < edges.flat().length * 0.9) return { fit: 'cover' };
    const mid = [0, 1, 2].map((i) => {
      const v = all.map((c) => c[i]).sort((x, y) => x - y);
      return Math.round(v[Math.floor(v.length / 2)]);
    });
    return { fit: 'packshot', c: mid };
  };

  /**
   * The photo's real pixels. naturalWidth is corrected for the srcset density, so a 832 px file
   * can report 512; an ImageBitmap gives the file's own size.
   */
  const readPhoto = async (img) => {
    try {
      const bmp = 'createImageBitmap' in window ? await createImageBitmap(img).catch(() => img) : img;
      const own = bmp !== img;
      const verdict = judge(bmp, own ? bmp.width : img.naturalWidth, own ? bmp.height : img.naturalHeight);
      if (own && bmp.close) bmp.close();
      return verdict;
    } catch (e) {
      /* tainted canvas (photo from another origin): read a small CORS copy instead */
    }
    return new Promise((resolve) => {
      try {
        const url = new URL(img.currentSrc || img.src, location.href);
        url.searchParams.set('width', '600');
        const copy = new Image();
        copy.crossOrigin = 'anonymous';
        copy.onload = () => {
          try {
            resolve(judge(copy, copy.naturalWidth, copy.naturalHeight));
          } catch (e) {
            resolve(null);
          }
        };
        copy.onerror = () => resolve(null);
        copy.src = url.href;
      } catch (e) {
        resolve(null);
      }
    });
  };

  const apply = (img, key, verdict) => {
    const box = img.closest(BOX);
    const packshot = verdict && verdict.fit === 'packshot';
    const white = packshot ? `rgb(${verdict.c.join(' ')})` : '';
    [img, box].forEach((el) => {
      if (!el) return;
      if (packshot) el.style.backgroundColor = white;
      else el.style.removeProperty('background-color');
    });
    img.dataset.menamGroundKey = key || '';
    img.setAttribute('data-menam-fit', packshot ? 'packshot' : 'cover');
  };

  const handle = (img) => {
    const key = keyOf(img);
    const known = key && store[key];
    if (known) {
      // Seen before: the right fit is set now, before (or while) the photo loads
      if (!done.has(img)) {
        done.add(img);
        apply(img, key, known);
      }
      return;
    }
    if (!img.complete) return; // the load listener comes back for it
    if (done.has(img)) return;
    done.add(img);
    if (!img.naturalWidth) {
      apply(img, key, null);
      return;
    }
    readPhoto(img)
      .catch(() => null)
      .then((verdict) => {
        if (key && verdict) {
          store[key] = verdict;
          save();
        }
        apply(img, key, verdict);
      });
  };

  const scan = (scope) => {
    scope.querySelectorAll(SELECTOR).forEach(handle);
  };

  // Photos that load later (lazy rows, the hover photo, filters, predictive search)
  document.addEventListener(
    'load',
    (event) => {
      const img = event.target;
      if (!(img instanceof HTMLImageElement) || !img.matches(SELECTOR)) return;
      // A different photo swapped into the same element (a srcset width change keeps the key)
      if (done.has(img) && 'menamGroundKey' in img.dataset && img.dataset.menamGroundKey !== keyOf(img)) {
        done.delete(img);
      }
      handle(img);
    },
    true
  );

  document.addEventListener(
    'error',
    (event) => {
      const img = event.target;
      if (img instanceof HTMLImageElement && img.matches(SELECTOR) && !done.has(img)) {
        done.add(img);
        apply(img, '', null);
      }
    },
    true
  );

  new MutationObserver((records) => {
    records.forEach((record) =>
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches(SELECTOR)) handle(node);
        else scan(node);
      })
    );
  }).observe(document.documentElement, { childList: true, subtree: true });

  scan(document);
})();
